/** Full wiki edit page with structured sections. */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../auth/api';
import MarkdownEditor from '../../components/wiki/MarkdownEditor';
import ClientFitEditor from '../../components/wiki/ClientFitEditor';
import ComplianceEditor from '../../components/wiki/ComplianceEditor';
import PublishDialog from '../../components/wiki/PublishDialog';
import { Save, Send, Eye, ArrowLeft } from 'lucide-react';

export default function WikiEntryEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [entry, setEntry] = useState(null);
    const [data, setData] = useState({
        title_en: '', title_vi: '',
        overview_en: '', overview_vi: '',
        capabilities_en: '', capabilities_vi: '',
        vulnerabilities_en: '', vulnerabilities_vi: '',
        client_fit_json: { agency_types: [], use_cases_en: '', use_cases_vi: '' },
        compliance_json: { tcvn_number: '', mps_cert_status: 'unknown', import_license_status: 'unknown', notes: '' },
        category_id: null,
        tags: '',
    });
    const [showPublish, setShowPublish] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (isNew) { setEntry(null); return; }
            try {
                const res = await api.get(`/wiki/entry/${id}`);
                setEntry(res.data);
                setData({
                    title_en: res.data.title_en || '',
                    title_vi: res.data.title_vi || '',
                    overview_en: res.data.overview_en || '',
                    overview_vi: res.data.overview_vi || '',
                    capabilities_en: res.data.capabilities_en || '',
                    capabilities_vi: res.data.capabilities_vi || '',
                    vulnerabilities_en: res.data.vulnerabilities_en || '',
                    vulnerabilities_vi: res.data.vulnerabilities_vi || '',
                    client_fit_json: typeof res.data.client_fit_json === 'object' ? res.data.client_fit_json : { agency_types: [], use_cases_en: '', use_cases_vi: '' },
                    compliance_json: typeof res.data.compliance_json === 'object' ? res.data.compliance_json : { tcvn_number: '', mps_cert_status: 'unknown', import_license_status: 'unknown', notes: '' },
                    category_id: res.data.category_id,
                    tags: res.data.tags || '',
                });
            } catch (e) { setError('Failed to load entry'); }
        };
        load();
    }, [id, isNew]);

    const save = async (status) => {
        setSaving(true);
        setError('');
        try {
            const payload = { ...data, client_fit: data.client_fit_json, compliance: data.compliance_json };
            if (isNew) {
                const res = await api.post('/wiki/entry/new', payload);
                navigate(`/wiki/edit/${res.data.id}`);
            } else {
                await api.post(`/wiki/entry/${id}/edit`, payload);
            }
            if (status === 'published') {
                await api.post(`/wiki/entry/${id || 'new'}/publish`);
                navigate(`/wiki/entry/${data.title_en || data.title_vi}`.toLowerCase().replace(/\s+/g, '-'));
            } else {
                setEntry({ ...entry, ...payload });
            }
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed');
        }
        setSaving(false);
    };

    if (preview) {
        return (
            <div className="max-w-5xl mx-auto p-6">
                <button onClick={() => setPreview(false)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4">
                    <ArrowLeft size={16} /> Back to editor
                </button>
                <PreviewView data={data} />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-xl font-bold text-white">{isNew ? 'New Wiki Entry' : 'Edit Wiki Entry'}</h1>
                <div className="flex gap-2">
                    <button onClick={() => setPreview(true)} className="flex items-center gap-2 px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm text-slate-200">
                        <Eye size={16} /> Preview
                    </button>
                    <button onClick={() => save('draft')} disabled={saving} className="px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm text-slate-200 disabled:opacity-50">
                        <Save size={16} /> Save Draft
                    </button>
                    <button onClick={() => setShowPublish(true)} disabled={saving} className="flex items-center gap-2 px-3 py-2 rounded bg-green-600 hover:bg-green-500 text-sm text-white disabled:opacity-50">
                        <Send size={16} /> Publish
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            <div className="space-y-6">
                <SectionCard title="Identity">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Title (EN) <span className="text-red-400">*</span></label>
                            <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" value={data.title_en} onChange={e => setData({ ...data, title_en: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Tiêu đề (VI) *</label>
                            <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" value={data.title_vi} onChange={e => setData({ ...data, title_vi: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Category</label>
                            <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" value={data.category_id || ''} onChange={e => setData({ ...data, category_id: e.target.value })} placeholder="e.g. cameras, access-control" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Tags (comma-separated)</label>
                            <input className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2.5 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50" value={data.tags} onChange={e => setData({ ...data, tags: e.target.value })} />
                        </div>
                    </div>
                </SectionCard>

                <SectionCard title="Overview">
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Overview (EN) <span className="text-red-400">*</span></label>
                            <MarkdownEditor value={data.overview_en} onChange={(v) => setData({ ...data, overview_en: v })} placeholder="Describe the product in English..." />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Tổng quan (VI)</label>
                            <MarkdownEditor value={data.overview_vi} onChange={(v) => setData({ ...data, overview_vi: v })} placeholder="Mô tả sản phẩm bằng tiếng Việt..." />
                        </div>
                    </div>
                </SectionCard>

                <SectionCard title="Capabilities">
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Capabilities (EN) <span className="text-red-400">*</span></label>
                            <MarkdownEditor value={data.capabilities_en} onChange={(v) => setData({ ...data, capabilities_en: v })} placeholder="List key capabilities in English..." />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Khả năng (VI)</label>
                            <MarkdownEditor value={data.capabilities_vi} onChange={(v) => setData({ ...data, capabilities_vi: v })} placeholder="Danh sách khả năng bằng tiếng Việt..." />
                        </div>
                    </div>
                </SectionCard>

                <SectionCard title="Vulnerabilities & Incidents">
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Vulnerabilities (EN)</label>
                            <MarkdownEditor value={data.vulnerabilities_en} onChange={(v) => setData({ ...data, vulnerabilities_en: v })} placeholder="List CVEs or known vulnerabilities in English..." />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-200 block mb-1">Lỗ hổng (VI)</label>
                            <MarkdownEditor value={data.vulnerabilities_vi} onChange={(v) => setData({ ...data, vulnerabilities_vi: v })} placeholder="Danh sách CVE hoặc lỗ hổng..." />
                        </div>
                    </div>
                </SectionCard>

                <SectionCard title="Client Fit">
                    <ClientFitEditor clientFit={data.client_fit_json} onChange={(cf) => setData({ ...data, client_fit_json: cf })} />
                </SectionCard>

                <SectionCard title="Compliance">
                    <ComplianceEditor compliance={data.compliance_json} onChange={(c) => setData({ ...data, compliance_json: c })} />
                </SectionCard>
            </div>

            {showPublish && <PublishDialog data={data} onPublish={() => { save('published'); setShowPublish(false); }} onCancel={() => setShowPublish(false)} />}
        </div>
    );
}

function SectionCard({ title, children }) {
    return (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
            <h2 className="text-base font-semibold text-amber-400 mb-3">{title}</h2>
            {children}
        </div>
    );
}

function PreviewView({ data }) {
    return (
        <div className="prose prose-invert max-w-none">
            <h1>{data.title_en || data.title_vi}</h1>
            <p className="text-slate-400 text-sm">Preview mode — this is how the published entry will look.</p>
            <h2>Overview</h2>
            <div dangerouslySetInnerHTML={{ __html: data.overview_en || data.overview_vi || '(not set)' }} />
            <h2>Capabilities</h2>
            <div dangerouslySetInnerHTML={{ __html: data.capabilities_en || data.capabilities_vi || '(not set)' }} />
            <h2>Vulnerabilities</h2>
            <div dangerouslySetInnerHTML={{ __html: data.vulnerabilities_en || data.vulnerabilities_vi || '(none set)' }} />
            <h2>Client Fit</h2>
            <p>Agencies: {data.client_fit_json?.agency_types?.join(', ') || '(none)'}</p>
            <h2>Compliance</h2>
            <p>TCVN: {data.compliance_json?.tcvn_number || '(not set)'} | MPS: {data.compliance_json?.mps_cert_status || 'unknown'}</p>
        </div>
    );
}